/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useNetwork from "@/stores/network";
import styled from "@emotion/styled";
import { usePreferenceStore } from "@/auth/stores/preference";
import { useGetValveReports } from "@/reports/api";
import {
  isManualValveReport,
  isSafetyValveReport,
  ManualValveReport,
  SafetyValveReport,
} from "@/reports/types/SafetyValveReport";
import usePaginatedTable from "@/hooks/usePaginatedTable";
import { Location } from "@/reports/config/locations";
import { withErrorBoundary } from "react-error-boundary";
import FallbackComponent from "@/components/FallbackComponent";
import {
  ALL,
  ARCHIVED,
  PUBLISHED,
  SIGNED_OFF,
  Status,
  W_ARCHIVE,
} from "@/reports/config/status";
import { ReportType } from "@/reports/types/Api";
import { Condition } from "@/types/Api";
import { useNavigate } from "react-router-dom";
import { spacing } from "@/config/theme";
import useRole from "@/auth/hooks/useRole";
import { isTablet } from "@/utils/deviceCheck";
import { ConfigProvider, Table as AntdTable, Button } from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import StyledTableEmptyState from "@/components/TableEmptyState";
import { getEmptyStateBaseOnStatus } from "@/reports/utils/report";
import {
  onlineTableColumns,
  getViewerTableColumns,
  getManualValveReportColumns,
} from "./columns";
import BulkExportBanner from "./BulkExportBanner";
import BulkChangeStatusBanner from "./BulkChangeStatusBanner";

const Table = styled(AntdTable)`
  .vv-number-column,
  .last-updated-column {
    .ant-table-column-title {
      flex: none;
    }
  }

  .last-updated-column {
    .ant-table-column-sorters {
      justify-content: flex-start;
    }
  }
` as any as typeof AntdTable;

const DEFAULT_STATUS_FILTER_FOR_ADMIN: Condition[] = [
  {
    column: "status",
    operator: "IN",
    value: "IN_PROGRESS",
  },
  {
    column: "status",
    operator: "IN",
    value: "FOR_REVIEW",
  },
  {
    column: "status",
    operator: "IN",
    value: "SIGNED_OFF",
  },
  {
    column: "status",
    operator: "IN",
    value: "PUBLISHED",
  },
  {
    column: "status",
    operator: "IN",
    value: "WAREHOUSE",
  },
];

const ClearFiltersButtonWrapper = styled.div`
  position: relative;
  bottom: -${spacing.xxxs};
`;

interface Props {
  location?: Location;
  reportType?: ReportType;
  status: Status;
  refreshReportTable?: () => void;
}
const ReportsTable = ({
  location,
  status,
  refreshReportTable,
  reportType = "SAFETY_VALVE",
}: Props) => {
  const navigate = useNavigate();
  const { online } = useNetwork();
  const {
    rememberedFilters,
    rememberSorter,
    rememberedManualValveFilters,
    rememberedManualValveSorter,
    setRememberedFilters,
    clearRememberedFilters,
    clearRememberedSorter,
    setRememberedSorter,
    setRememberedManualValveFilters,
    setRememberedManualValveSorter,
    clearRememberedManualValveFilters,
    clearRememberedManualValveSorter,
  } = usePreferenceStore();
  const { isTechnician, isAdmin, isViewer } = useRole();
  const statusCondition = useMemo<Condition[]>(() => {
    if (status === ALL) {
      const excludeStatuses = [ARCHIVED];

      // Exclude W_ARCHIVE status for technicians
      if (isTechnician) {
        excludeStatuses.push(W_ARCHIVE);
      }

      return excludeStatuses.map((item) => ({
        column: "status",
        operator: "NOT_IN",
        value: item,
      }));
    }

    return [
      {
        column: "status",
        operator: "EQUALS",
        value: status,
      },
    ];
  }, [status, isTechnician]);

  const {
    pagination,
    setPagination,
    onChange,
    columns: safetyValveReportColumns,
    filters,
    sorter,
    setFilters,
  } = usePaginatedTable<SafetyValveReport>({
    columns: !isViewer
      ? onlineTableColumns(status, isAdmin)
      : getViewerTableColumns(status),
    initialFilters: rememberedFilters,
    initialSorter: rememberSorter,
  });

  const {
    pagination: manualValvePagination,
    setPagination: setManualValvePagination,
    onChange: onManualValveChange,
    columns: manualValveReportColumns,
    filters: manualValveFilters,
    sorter: manualValveSorter,
    setFilters: setManualValveFilters,
  } = usePaginatedTable<ManualValveReport>({
    columns: getManualValveReportColumns(status, isAdmin),
    initialFilters: rememberedManualValveFilters,
    initialSorter: rememberedManualValveSorter,
  });

  useEffect(() => {
    if (reportType === "SAFETY_VALVE") {
      setRememberedFilters(filters);
      setRememberedSorter(sorter);
    } else if (reportType === "MANUAL_VALVE") {
      setRememberedManualValveFilters(manualValveFilters);
      setRememberedManualValveSorter(manualValveSorter);
    }
  }, [
    filters,
    sorter,
    manualValveFilters,
    manualValveSorter,
    setRememberedFilters,
    setRememberedSorter,
    reportType,
    setRememberedManualValveFilters,
    setRememberedManualValveSorter,
  ]);

  const removeMultipleStatusFilter = useCallback(
    (currentFilters: Condition[]) => {
      const filtered = currentFilters.filter(
        (filter) => filter.operator !== "IN"
      );
      // NOTE: We add IN operator here which is used for multiple status selection
      if (status === ALL) {
        if (isAdmin) return [...filtered, ...DEFAULT_STATUS_FILTER_FOR_ADMIN];
      }
      return filtered;
    },
    [status, isAdmin]
  );

  // Remove multiple status filter when status not equal to ALL
  useEffect(() => {
    if (reportType === "SAFETY_VALVE") {
      setFilters((currentFilters) => {
        return removeMultipleStatusFilter(currentFilters);
      });
    }
    if (reportType === "MANUAL_VALVE") {
      setManualValveFilters((currentFilters) => {
        return removeMultipleStatusFilter(currentFilters);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const {
    isLoading,
    isError,
    error,
    data: onlineData,
  } = useGetValveReports({
    params: {
      limit:
        reportType === "SAFETY_VALVE"
          ? pagination.pageSize
          : manualValvePagination.pageSize,
      page:
        reportType === "SAFETY_VALVE"
          ? pagination.current
          : manualValvePagination.current,
      sorter,
      ...(isViewer
        ? {
            search: [
              ...(reportType === "SAFETY_VALVE" ? filters : manualValveFilters),
              ...statusCondition,
            ],
          }
        : {
            search: [
              ...(reportType === "SAFETY_VALVE" ? filters : manualValveFilters),
              ...statusCondition,
            ],
            location,
          }),
    },
    isViewer,
    reportType,
  });

  const handleRowClick = useCallback(
    (record: SafetyValveReport | ManualValveReport) => {
      if (isAdmin || isTechnician) {
        if (isSafetyValveReport(record)) {
          navigate(
            `/safety-valve-reports/edit/${location}/${record.SafetyValveReportId}`
          );
        }
        if (isManualValveReport(record)) {
          navigate(
            `/manual-valve-reports/edit/${location}/${record.ManualValveRepairReportId}`
          );
        }
      }
      if (isViewer) {
        if (isSafetyValveReport(record)) {
          navigate(
            `/safety-valve-reports/view/${record.Location}/${record.SafetyValveReportId}`
          );
        }
        if (isManualValveReport(record)) {
          navigate(
            `/manual-valve-reports/view/${record.Location}/${record.ManualValveRepairReportId}`
          );
        }
      }
    },
    [isAdmin, isTechnician, isViewer, navigate, location]
  );

  const onRow = useCallback(
    (record: SafetyValveReport | ManualValveReport) => {
      return {
        onClick: () => handleRowClick(record),
        style: { cursor: "pointer" },
      };
    },
    [handleRowClick]
  );

  // States for bulk actions
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<
    (SafetyValveReport | ManualValveReport)[]
  >([]);

  const rowSelectionBaseOnStatus = ():
    | TableRowSelection<SafetyValveReport | ManualValveReport>
    | undefined => {
    if (!online) return undefined;

    const defaultRowSelection: TableRowSelection<
      SafetyValveReport | ManualValveReport
    > = {
      selectedRowKeys,
      onChange: (updatedSelectedRowKeys: React.Key[], rows) => {
        setSelectedRowKeys(updatedSelectedRowKeys);
        setSelectedRows(rows);
      },
    };

    // Bulk action for exporting reports in Published status
    if (status === PUBLISHED) {
      return { ...defaultRowSelection, hideSelectAll: true };
    }

    // Bulk action for changing status in Signed Off status
    if (status === SIGNED_OFF && isAdmin) {
      return defaultRowSelection;
    }

    return undefined;
  };

  // Clear selected row keys when location or status or online changes
  useEffect(() => {
    // Statuses that allow multiple selection
    const multipleSelectionStatus: Status[] = [PUBLISHED, SIGNED_OFF];

    if (location || multipleSelectionStatus.includes(status) || !online) {
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  }, [location, status, online]);

  if (isError) throw error;

  useEffect(() => {
    if (onlineData) {
      if (reportType === "SAFETY_VALVE") {
        setPagination({
          current: onlineData?.page,
          total: onlineData?.totalDocs,
          pageSize: onlineData?.limit,
        });
      } else if (reportType === "MANUAL_VALVE") {
        setManualValvePagination({
          current: onlineData?.page,
          total: onlineData?.totalDocs,
          pageSize: onlineData?.limit,
        });
      }
    }
  }, [onlineData, setPagination, setManualValvePagination, reportType]);

  const tableDefaultProps = {
    loading: isLoading,
    scroll: isTablet ? { x: 768 } : undefined,
    pagination: {
      ...(reportType === "SAFETY_VALVE" ? pagination : manualValvePagination),
      showSizeChanger: false,
      hideOnSinglePage: true,
      onChange: () => {
        // Clear selected row keys when page changes
        setSelectedRowKeys([]);
      },
    },
  };

  return (
    <ConfigProvider
      // Display different status empty state when there is no data
      renderEmpty={() => {
        const { imageUrl, description } = getEmptyStateBaseOnStatus(
          status,
          isViewer,
          // Filters other than status means user is searching
          filters.some((filter) => filter.column !== "status")
        );
        return (
          <StyledTableEmptyState image={imageUrl} description={description} />
        );
      }}
    >
      {selectedRowKeys.length > 0 && status === PUBLISHED && (
        <BulkExportBanner
          selectedRows={selectedRows}
          setSelectedRowKeys={setSelectedRowKeys}
          location={location}
          reportType={reportType}
        />
      )}

      {/* Display bulk change status banner when status is Signed Off */}
      {selectedRowKeys.length > 0 && status === SIGNED_OFF && (
        <BulkChangeStatusBanner
          selectedRows={selectedRows}
          setSelectedRowKeys={setSelectedRowKeys}
          location={location}
          reportType={reportType}
        />
      )}
      <ClearFiltersButtonWrapper>
        <Button
          onClick={() => {
            if (reportType === "SAFETY_VALVE") {
              setFilters([]);
              // Reset status filter to default values (without W-ARCHIVE for admins)
              clearRememberedFilters(
                isAdmin ? DEFAULT_STATUS_FILTER_FOR_ADMIN : []
              );
              clearRememberedSorter();
            } else if (reportType === "MANUAL_VALVE") {
              setManualValveFilters([]);
              clearRememberedManualValveFilters(
                isAdmin ? DEFAULT_STATUS_FILTER_FOR_ADMIN : []
              );
              clearRememberedManualValveSorter();
            }
            // Force re-render table when filters are cleared to reset to default
            refreshReportTable?.();
          }}
        >
          Clear Filters and Sorting
        </Button>
      </ClearFiltersButtonWrapper>
      {reportType === "SAFETY_VALVE" && (
        <Table<SafetyValveReport>
          dataSource={(onlineData?.docs as SafetyValveReport[]) || []}
          columns={safetyValveReportColumns}
          onChange={onChange}
          rowKey={(row) => `${row.vCustomer}-${row.SafetyValveReportId}`}
          // Disable row click when user using bulk actions
          onRow={selectedRowKeys.length === 0 ? onRow : undefined}
          rowSelection={
            rowSelectionBaseOnStatus() as TableRowSelection<SafetyValveReport>
          }
          {...tableDefaultProps}
        />
      )}
      {reportType === "MANUAL_VALVE" && (
        <Table<ManualValveReport>
          dataSource={(onlineData?.docs as ManualValveReport[]) || []}
          columns={manualValveReportColumns}
          onChange={onManualValveChange}
          rowKey={(row) => `${row.vCustomer}-${row.ManualValveRepairReportId}`}
          onRow={selectedRowKeys.length === 0 ? onRow : undefined}
          rowSelection={
            rowSelectionBaseOnStatus() as TableRowSelection<ManualValveReport>
          }
          {...tableDefaultProps}
        />
      )}
    </ConfigProvider>
  );
};

export default withErrorBoundary(ReportsTable, {
  FallbackComponent,
});
